import { MediaItem } from '../../../api/structs/media';
import { MediaLibraryItem } from '../../../api/media';
import { VideoCapabilities } from '../../../api/videoCapabilities';

/**
 * Reference INPUTS for an `ai_video` generation — as opposed to the anchor
 * FRAMES in `./referenceFrames`.
 *
 * A frame is where the clip starts (or ends); a reference is subject or style
 * material the model is asked to carry INTO the shot. Some models take both,
 * some declare them mutually exclusive, and the registry is what says which
 * (`reference_constraints.exclusions`, read through `excluded()`).
 *
 * The wire shape is deliberately flatter than a frame's: Go
 * `AIVideoSettings.ReferenceImages/Audios/Videos` are plain `[]string`, each
 * entry a MEDIA ID or a URL, in wiring order. The id is the better half to
 * send — the backend resolves it to the item's current file, so a reference
 * keeps pointing at the right take after that still is regenerated, which a
 * captured URL does not. A URL stays accepted for material that is not ours.
 *
 * That flatness is why `ReferenceEntry` carries a separate `url`: it is the
 * picture to PAINT, and a list read back off a saved item may legitimately
 * have none (an opaque media id). Nothing may invent one — a wrong <img src>
 * shows the operator a reference the generation is not using.
 */
export type ReferenceInputKind = 'images' | 'audios' | 'videos';

/** The kinds in the order they are offered. */
export const REFERENCE_INPUT_KINDS: ReferenceInputKind[] = ['images', 'audios', 'videos'];

export interface ReferenceEntry {
  /** What goes on the wire: the media id when we have one, else the URL. */
  value: string;
  /** What to paint. Empty for an entry that is an opaque media id. */
  url: string;
}

export interface ReferenceInputState {
  images: ReferenceEntry[];
  audios: ReferenceEntry[];
  videos: ReferenceEntry[];
}

export interface ReferenceInputsPayload {
  reference_images: string[];
  reference_audios: string[];
  reference_videos: string[];
}

export const emptyReferenceInputs = (): ReferenceInputState => ({
  images: [],
  audios: [],
  videos: [],
});

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Paintable right now: an absolute URL, a served path, or an inline data URI. */
const paintableUrl = (value: string): string =>
  /^(https?:)?\/\//.test(value) || value.startsWith('/') || value.startsWith('data:') ? value : '';

const entryFromValue = (value: string): ReferenceEntry => ({
  value,
  url: paintableUrl(value),
});

const readList = (raw: unknown): ReferenceEntry[] =>
  Array.isArray(raw)
    ? raw
        .map((entry) => trimmed(entry))
        .filter((value) => value !== '')
        .map(entryFromValue)
    : [];

/** What a saved video declares as its reference inputs. */
export const readReferenceInputs = (item: MediaItem | undefined | null): ReferenceInputState => {
  const aiVideo = (item?.output_spec as Record<string, any> | undefined)?.ai_video;
  if (!aiVideo || typeof aiVideo !== 'object') return emptyReferenceInputs();
  return {
    images: readList(aiVideo.reference_images),
    audios: readList(aiVideo.reference_audios),
    videos: readList(aiVideo.reference_videos),
  };
};

/**
 * One library row as a reference entry — the media id preferred over the URL,
 * with the URL kept for the thumbnail. Null when the row names neither.
 */
export const entryFromLibraryItem = (item: MediaLibraryItem): ReferenceEntry | null => {
  const url = trimmed(item.url) || trimmed(item.preview_url) || trimmed(item.thumbnail_url);
  const value = trimmed(item.media_id) || trimmed(item.id) || url;
  if (!value) return null;
  return { value, url };
};

/**
 * The three wire lists. EMPTY LISTS INCLUDED — the backend's
 * `mergeVideoReferenceLists` reads a present list as authoritative and a
 * missing one as "no opinion", so a cleared slot has to be sent as `[]` or
 * the stored references silently survive the clear.
 */
export const buildReferenceInputsPayload = (
  state: ReferenceInputState
): ReferenceInputsPayload => ({
  reference_images: state.images.map((entry) => entry.value),
  reference_audios: state.audios.map((entry) => entry.value),
  reference_videos: state.videos.map((entry) => entry.value),
});

const sameList = (a: ReferenceEntry[], b: ReferenceEntry[]): boolean =>
  a.length === b.length && a.every((entry, index) => entry.value === b[index].value);

/**
 * True once the user has actually moved something. Compared on the WIRE value
 * only: the paint URL is display state, and a refreshed thumbnail is not an
 * edit the backend should be told about.
 */
export const isReferenceInputStateDirty = (
  initial: ReferenceInputState,
  current: ReferenceInputState
): boolean =>
  !sameList(initial.images, current.images) ||
  !sameList(initial.audios, current.audios) ||
  !sameList(initial.videos, current.videos);

/** How many references are bound across every kind. */
export const countReferenceInputs = (state: ReferenceInputState): number =>
  state.images.length + state.audios.length + state.videos.length;

/** How many inputs of this kind the model declares it takes (0 = unsupported). */
export const referenceCapOf = (caps: VideoCapabilities, kind: ReferenceInputKind): number => {
  if (kind === 'images') return caps.referenceImages;
  if (kind === 'audios') return caps.referenceAudios;
  return caps.referenceVideos;
};

const hasEntries = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

/**
 * A regenerate spec with the stored reference lists this model cannot take
 * forced to EXPLICITLY EMPTY.
 *
 * The regenerate spec is built off the item's own `output_spec`, so lists saved
 * against a PREVIOUS model ride along to a newly chosen one untouched by the
 * editor — and on a model that declares the kind unsupported the editor renders
 * no row for them at all, so they are invisible state that is submitted
 * anyway. Cleared rather than omitted, because omission is not a clear:
 * `mergeVideoReferenceLists` reads an absent key as "no opinion" and leaves the
 * stored list bound.
 *
 * A kind the model DOES declare is left exactly as stored — this drops what
 * cannot be used, it does not second-guess what can.
 *
 * Only ever called with KNOWN capabilities: the all-off shape also stands for
 * "the registry did not answer", and clearing a list on the strength of an
 * outage would throw away the operator's work.
 */
export const withoutUnsupportedReferenceLists = <T extends Partial<ReferenceInputsPayload>>(
  aiVideo: T,
  caps: VideoCapabilities
): T => {
  const next: T = { ...aiVideo };
  if (caps.referenceImages === 0 && hasEntries(next.reference_images)) next.reference_images = [];
  if (caps.referenceAudios === 0 && hasEntries(next.reference_audios)) next.reference_audios = [];
  if (caps.referenceVideos === 0 && hasEntries(next.reference_videos)) next.reference_videos = [];
  return next;
};
